import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import logoIcon from '../assets/logo-icon-exact.png';
import UpgradeButton from '../components/UpgradeButton';
import Footer from '../components/Footer';

const DAILY_GOAL_STORAGE_KEY = 'trp_daily_goal_target';
const DEFAULT_DAILY_GOAL_TARGET = 20;
const STRIPE_MONTHLY = 'https://buy.stripe.com/4gMaEW7lb0sOcPDa7q5ZC04';

function parseDailyGoalTarget(value) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return parsed;
}

function Settings({ session, darkMode, toggleDark, isSubscribed, hasFullAccess, trialEndsAt }) {
  const navigate = useNavigate();

  const [newPassword, setNewPassword]     = useState('');
  const [confirmPw, setConfirmPw]         = useState('');
  const [pwLoading, setPwLoading]         = useState(false);
  const [pwError, setPwError]             = useState('');
  const [pwSuccess, setPwSuccess]         = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError]     = useState('');

  const [subscription, setSubscription]   = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError]     = useState('');

  const [gradingMode, setGradingMode] = useState(
    () => localStorage.getItem('trp_grading_mode') || 'immediate'
  );
  const [dailyGoalTarget, setDailyGoalTarget] = useState(
    () => localStorage.getItem(DAILY_GOAL_STORAGE_KEY) || String(DEFAULT_DAILY_GOAL_TARGET)
  );
  const [dailyGoalError, setDailyGoalError] = useState('');
  const [dailyGoalSaved, setDailyGoalSaved] = useState(false);

  const handleGradingMode = (mode) => {
    setGradingMode(mode);
    localStorage.setItem('trp_grading_mode', mode);
  };

  const handleDailyGoalSave = () => {
    if (!session || !isSubscribed) {
      setDailyGoalError('Daily goal can only be set by signed-in subscribers.');
      setDailyGoalSaved(false);
      return;
    }
    const parsed = parseDailyGoalTarget(dailyGoalTarget);
    if (!parsed) {
      setDailyGoalError('Enter a whole number greater than 0.');
      setDailyGoalSaved(false);
      return;
    }
    localStorage.setItem(DAILY_GOAL_STORAGE_KEY, String(parsed));
    setDailyGoalTarget(String(parsed));
    setDailyGoalError('');
    setDailyGoalSaved(true);
  };

  useEffect(() => {
    if (!session) return;
    supabase
      .from('subscriptions')
      .select('status, plan, current_period_end, trial_ends_at')
      .eq('user_id', session.user.id)
      .single()
      .then(({ data }) => setSubscription(data || null));
  }, [session]);

  const handleSignOut = async () => { await supabase.auth.signOut(); navigate('/'); };

  const paymentPending = !!session && !isSubscribed && !hasFullAccess && !!sessionStorage.getItem('trp_pending_checkout');
  const pendingCheckoutUrl = session?.user?.id
    ? `${STRIPE_MONTHLY}?client_reference_id=${encodeURIComponent(session.user.id)}`
    : '/subscribe';

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setPortalError('');
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: { user_id: session.user.id, return_url: window.location.href },
    });
    if (error || !data?.url) {
      let detail = data?.error || error?.message || 'Unknown error';
      // FunctionsHttpError carries the response body under context
      if (!data?.error && error?.context) {
        try { const body = await error.context.json(); detail = body?.error || detail; } catch (_) {}
      }
      console.error('Billing portal error:', detail, { data, error });
      setPortalError(`Could not open billing portal: ${detail}`);
    } else {
      window.location.href = data.url;
    }
    setPortalLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPw) { setPwError('Passwords do not match.'); return; }
    setPwLoading(true); setPwError('');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setPwError(error.message);
    } else {
      setPwSuccess(true);
      setNewPassword(''); setConfirmPw('');
    }
    setPwLoading(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true); setDeleteError('');
    const { error } = await supabase.functions.invoke('delete-account', {});
    if (error) {
      setDeleteError('Could not delete account. Please try again or contact support.');
      setDeleteLoading(false);
      return;
    }
    await supabase.auth.signOut();
    navigate('/');
  };

  const hasActiveTrial = !!trialEndsAt && new Date(trialEndsAt) > new Date();
  const trialDaysLeft = hasActiveTrial
    ? Math.max(1, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  let currentStatusLabel = 'Practice Mode';
  let currentStatusClass = 'sub-badge sub-badge-free';
  let currentStatusDesc = 'Limited mode: full Section 4 and 3 questions per other section.';

  if (isSubscribed || subscription?.status === 'active' || subscription?.status === 'trialing') {
    currentStatusLabel = 'Subscribed';
    currentStatusClass = 'sub-badge sub-badge-active';
    const planLabel = subscription?.plan === 'annual' ? 'Annual plan' : 'Monthly plan';
    currentStatusDesc = subscription?.current_period_end
      ? `${planLabel} active until ${new Date(subscription.current_period_end).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}.`
      : `${planLabel} is active.`;
  } else if (hasActiveTrial || (hasFullAccess && !isSubscribed)) {
    currentStatusLabel = 'Promo Trial';
    currentStatusClass = 'sub-badge sub-badge-free';
    currentStatusDesc = `Trial access active${trialDaysLeft > 0 ? ` (${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining)` : ''}.`;
  } else if (subscription?.status === 'past_due') {
    currentStatusLabel = 'Past Due';
    currentStatusClass = 'sub-badge sub-badge-past_due';
    currentStatusDesc = 'Payment needs attention. Update billing to restore full access.';
  } else if (subscription?.status === 'cancelled') {
    currentStatusLabel = 'Cancelled';
    currentStatusClass = 'sub-badge sub-badge-cancelled';
    currentStatusDesc = 'Subscription is cancelled. You are currently in free practice mode.';
  }

  return (
    <div className="settings-page">
      <header>
        <div className="logo" onClick={() => navigate(session ? '/quiz' : '/home')}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span> Pro</span></div>
        </div>
        <div className="header-right">
          {session && <Link to="/quiz" className="dark-toggle">Home</Link>}
          {session && <Link to="/dashboard" className="dark-toggle header-hide-mobile">My Scores</Link>}
          {session && <button className="dark-toggle header-hide-mobile" onClick={handleSignOut}>Sign Out</button>}
          {!session && <Link to="/login" className="dark-toggle">Login</Link>}
          {!session && <Link to="/quiz" className="dark-toggle">Home</Link>}
          {!session && <Link to="/subscribe" className="btn-subscribe header-hide-mobile">Subscribe</Link>}
          <Link to="/settings" className="dark-toggle btn-gear-active header-hide-mobile" title="Settings">⚙️</Link>
        </div>
      </header>

      <div className="settings-wrap">
        <button className="settings-back" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="settings-title">Settings</h1>

        {/* ── APPEARANCE ─────────────────────────────────────────── */}
        <div className="settings-section">
          <div className="settings-section-title">Appearance</div>
          <div className="settings-row">
            <div className="settings-row-info">
              <div className="settings-row-label">Dark Mode</div>
              <div className="settings-row-desc">Switch between light and dark theme</div>
            </div>
            <button
              className={`settings-toggle${darkMode ? ' on' : ''}`}
              onClick={toggleDark}
              aria-label="Toggle dark mode"
            >
              <div className="settings-toggle-knob" />
            </button>
          </div>
        </div>

        {/* ── QUIZ GRADING MODE ──────────────────────────────────── */}
        <div className="settings-section">
          <div className="settings-section-title">Quiz Grading Mode</div>
          <div className="settings-row">
            <div className="settings-row-info">
              <div className="settings-row-label">Immediate Feedback</div>
              <div className="settings-row-desc">Questions are marked as you answer each one, with explanations shown immediately.</div>
            </div>
            <button
              className={`settings-toggle${gradingMode === 'immediate' ? ' on' : ''}`}
              onClick={() => handleGradingMode('immediate')}
              aria-label="Immediate feedback mode"
            >
              <div className="settings-toggle-knob" />
            </button>
          </div>
          <div className="settings-row" style={{ borderBottom: 'none' }}>
            <div className="settings-row-info">
              <div className="settings-row-label">Exam Mode</div>
              <div className="settings-row-desc">No feedback during the quiz. Submit at the end to see your full grade and question review.</div>
            </div>
            <button
              className={`settings-toggle${gradingMode === 'exam' ? ' on' : ''}`}
              onClick={() => handleGradingMode('exam')}
              aria-label="Exam mode"
            >
              <div className="settings-toggle-knob" />
            </button>
          </div>
        </div>

        {/* ── DAILY GOAL ────────────────────────────────────────── */}
        <div className="settings-section">
          <div className="settings-section-title">Daily Questions Goal</div>
          <div className="settings-row" style={{ borderBottom: 'none' }}>
            <div className="settings-row-info">
              <div className="settings-row-label">Questions per day</div>
              {session && isSubscribed ? (
                <>
                  <div className="settings-row-desc">Set any whole-number target. The sidebar progress tracker uses this number.</div>
                  <div className="settings-goal-controls">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="settings-goal-input"
                      value={dailyGoalTarget}
                      onChange={e => {
                        setDailyGoalTarget(e.target.value);
                        setDailyGoalError('');
                        setDailyGoalSaved(false);
                      }}
                      aria-label="Daily questions goal"
                    />
                    <button className="btn-settings-action" onClick={handleDailyGoalSave}>
                      Save Goal
                    </button>
                  </div>
                  {dailyGoalError && <div className="settings-error" style={{ marginTop: '0.45rem' }}>{dailyGoalError}</div>}
                  {dailyGoalSaved && !dailyGoalError && <div className="settings-success" style={{ marginTop: '0.45rem' }}>Daily goal saved.</div>}
                </>
              ) : (
                <div className="settings-row-desc">This setting is available to signed-in subscribers only.</div>
              )}
            </div>
          </div>
        </div>

        {/* ── YOUR PLAN (free users) ─────────────────────────────── */}
        {!session && (
          <>
            <div className="settings-section">
              <div className="settings-section-title">Your Plan</div>

              <div className="settings-plan-current">
                <span className="settings-plan-badge">Practice Mode</span>
                <span className="settings-plan-current-desc">
                  You have access to two complete free quizzes and a 3-question preview of every other section.
                </span>
              </div>

              <div className="settings-plan-divider" />

              <div className="settings-plan-features-title">Everything included with a subscription</div>
              <ul className="settings-feature-list">
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>All 2,093+ electrical questions</strong> — complete coverage of every section, not just the preview</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Every licence-focused track</strong> — Master Electrician, 309A, and 442A</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Randomized question order</strong> — every session is different so you're testing knowledge, not memory</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Smart Quiz access</strong> — target missed questions and weakest sections automatically</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Exam Simulator</strong> — timed mock exams to practice real-test pacing and pressure</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Progress dashboard</strong> — track your scores over time and see exactly where you're improving</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Achievement badges</strong> — milestones like Perfect Score, Sharp Mind, and Expert as you progress</span>
                </li>
                <li>
                  <span className="sfl-check">✓</span>
                  <span><strong>Percentile ranking</strong> — see how your scores compare to other candidates preparing for the same exam</span>
                </li>
              </ul>

              <div className="settings-plan-cta">
                <Link to="/subscribe" className="btn-settings-subscribe">
                  Get Full Access →
                </Link>
                <div className="settings-plan-pricing-note">
                  <span className="price-was">$24.99</span> $9.99 / month &nbsp;·&nbsp; $199.00 / year <span className="settings-plan-save">save 34%</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">Account</div>
              <div className="settings-row" style={{ borderBottom: 'none' }}>
                <div className="settings-row-info">
                  <div className="settings-row-label">Already a subscriber?</div>
                  <div className="settings-row-desc">Sign in to access your account and manage your subscription.</div>
                </div>
              </div>
              <div style={{ paddingTop: '0.5rem' }}>
                <Link to="/login" className="btn-settings-action" style={{ textDecoration: 'none', display: 'inline-block' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </>
        )}
        {session && (
          <div className="settings-section">
            <div className="settings-section-title">Account &amp; Subscription</div>

            {paymentPending && (
              <div className="settings-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="settings-row-info">
                  <div className="settings-row-label">Finish Your Subscription</div>
                  <div className="settings-row-desc" style={{ marginBottom: '0.7rem' }}>
                    Your account is ready, but your subscription isn’t complete yet. Finish checkout to unlock the vault and premium features.
                  </div>
                  <a href={pendingCheckoutUrl} className="btn-settings-action" style={{ textDecoration: 'none', display: 'inline-block' }}>
                    Complete Subscription →
                  </a>
                </div>
              </div>
            )}

            {/* Current Status */}
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-label">Current Status</div>
                <div className="settings-row-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span className={currentStatusClass}>{currentStatusLabel}</span>
                </div>
                <div className="settings-row-desc">{currentStatusDesc}</div>
              </div>
            </div>

            {/* Email */}
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-label">Email Address</div>
                <div className="settings-row-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {session.user.email}
                  {hasFullAccess && !isSubscribed && trialEndsAt && (
                    <span className="sub-badge sub-badge-free">Promo Trial</span>
                  )}
                </div>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-label">Subscription</div>
                {subscription?.status === 'active' ? (
                  <div>
                    <div className="settings-row-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span className="sub-badge sub-badge-active">Active</span>
                      <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                        {subscription.plan === 'annual' ? 'Annual Plan' : 'Monthly Plan'}
                      </span>
                    </div>
                    <div className="settings-row-desc">
                      Renews {new Date(subscription.current_period_end).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <button
                        className="btn-settings-action"
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                      >
                        {portalLoading ? 'Opening…' : 'Manage Subscription →'}
                      </button>
                      {portalError && <div className="settings-error" style={{ marginTop: '0.5rem' }}>{portalError}</div>}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '0.5rem' }}>
                    <UpgradeButton
                      session={session}
                      isSubscribed={isSubscribed}
                      hasFullAccess={hasFullAccess}
                      trialEndsAt={trialEndsAt}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Change Password */}
            <div className="settings-row-group">
              <div className="settings-row-label" style={{ marginBottom: '0.75rem' }}>
                Change Password
              </div>
              {pwSuccess ? (
                <div className="settings-success">Password updated successfully.</div>
              ) : (
                <form onSubmit={handleChangePassword}>
                  {pwError && <div className="settings-error">{pwError}</div>}
                  <div className="form-group">
                    <input
                      type="password" value={newPassword}
                      onChange={e => { setNewPassword(e.target.value); setPwSuccess(false); setPwError(''); }}
                      placeholder="New password (min 6 characters)"
                      minLength={6} required
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="password" value={confirmPw}
                      onChange={e => { setConfirmPw(e.target.value); setPwError(''); }}
                      placeholder="Confirm new password"
                      minLength={6} required
                    />
                  </div>
                  <button type="submit" className="btn-settings-action" disabled={pwLoading}>
                    {pwLoading ? 'Updating…' : 'Update Password'}
                  </button>
                </form>
              )}
            </div>

            {/* Support */}
            <div className="settings-row-group">
              <div className="settings-row-label">Support</div>
              <div className="settings-row-desc" style={{ margin: '0.3rem 0 0.75rem' }}>
                Questions about your account, billing, or the app? We read every email and reply within 48 hours.
              </div>
              <Link to="/contact" className="btn-settings-action" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Contact Us →
              </Link>
            </div>

            {/* Delete Account */}
            <div className="settings-row-group">
              <div className="settings-row-label">Delete Account</div>
              <div className="settings-row-desc" style={{ margin: '0.3rem 0 0.85rem' }}>
                Permanently deletes your account, cancels your subscription, and removes all quiz progress. Cannot be undone.
              </div>
              {!showDeleteConfirm ? (
                <button
                  className="btn-settings-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Account
                </button>
              ) : (
                <div className="settings-confirm-box">
                  <div className="settings-confirm-msg">
                    ⚠️ Are you sure? This will cancel your subscription and permanently delete all your progress. This cannot be undone.
                  </div>
                  {deleteError && <div className="settings-error">{deleteError}</div>}
                  <div className="settings-confirm-actions">
                    <button
                      className="btn-settings-danger"
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? 'Deleting…' : 'Yes, delete my account'}
                    </button>
                    <button
                      className="btn-settings-cancel"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                      disabled={deleteLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Settings;
